import React from "react";
import { StatCard } from "@admin/components/Primitives";
import { paise, num } from "@admin/components/analytics/AnalyticsPrimitives";

const STATUS = {
  ready: { label: "Ready", cls: "bg-emerald-100 text-emerald-800" },
  processing: { label: "Processing", cls: "bg-sky-100 text-sky-800" },
  paid: { label: "Paid", cls: "bg-[#0A2540] text-white" },
};

export function PayoutQueueView({ q, previewing, onPreview, onViewDetail }) {
  return (
    <div data-testid="payout-queue-view">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <StatCard testid="payout-period" label="Settlement period" value={q.period.label} hint={`${q.period.cycle} · IST`} accent="navy" />
        <StatCard testid="payout-lawyers" label="Lawyers ready" value={num(q.summary.lawyers_ready)} hint="with eligible balances" accent="gold" />
        <StatCard testid="payout-eligible" label="Total eligible" value={paise(q.summary.eligible_paise)} hint="whole earnings, 48h window passed" accent="emerald" />
        <StatCard testid="payout-entries" label="Eligible earnings" value={num(q.summary.eligible_count)} hint="consultation earnings" />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="payout-queue-table">
        {q.rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400" data-testid="payout-queue-empty">No lawyers with eligible or in-progress settlements right now.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>{["Lawyer", "Eligible earnings", "Consultations", "Status", "Action"].map((h) => <th key={h} className="text-left font-semibold px-4 py-3 text-xs uppercase tracking-wider">{h}</th>)}</tr>
            </thead>
            <tbody>
              {q.rows.map((r) => {
                const st = STATUS[r.status] || STATUS.processing;
                return (
                  <tr key={r.lawyer_id} className="border-t border-slate-100" data-testid={`payout-row-${r.lawyer_id}`}>
                    <td className="px-4 py-3 font-bold text-[#0A2540]">{r.lawyer_name}</td>
                    <td className="px-4 py-3 font-extrabold text-[#0A2540]" data-testid={`payout-eligible-${r.lawyer_id}`}>{paise(r.eligible_paise)}</td>
                    <td className="px-4 py-3" data-testid={`payout-count-${r.lawyer_id}`}>{num(r.eligible_count)}</td>
                    <td className="px-4 py-3"><span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${st.cls}`} data-testid={`payout-status-${r.lawyer_id}`}>{st.label}</span>
                      {r.processing_paise > 0 && <span className="ml-2 text-[11px] text-slate-500">{paise(r.processing_paise)} in processing</span>}</td>
                    <td className="px-4 py-3">
                      {r.status === "ready" ? (
                        <button type="button" onClick={() => onPreview(r)} disabled={previewing === r.lawyer_id} className="px-3 py-1.5 rounded-full bg-[#0A2540] text-white text-xs font-bold hover:bg-[#123560] transition-colors disabled:opacity-60" data-testid={`create-settlement-${r.lawyer_id}`}>{previewing === r.lawyer_id ? "Loading…" : "Create Settlement"}</button>
                      ) : (
                        <button type="button" onClick={() => onViewDetail(r.latest_payout.payout_id)} className="px-3 py-1.5 rounded-full bg-slate-100 text-[#0A2540] text-xs font-bold hover:bg-slate-200 transition-colors" data-testid={`view-settlement-${r.lawyer_id}`}>View</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <p className="mt-3 text-[11px] text-slate-400" data-testid="payout-queue-note">Eligibility is computed server-side (completed + 48h refund window + not refunded + not already settled) and re-checked when a settlement is created. Earnings that become eligible later roll into the next weekly settlement.</p>
    </div>
  );
}
